import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";

import { useChat } from "@/api/chats";
import { useUpdateCredentials } from "@/api/verification";

import { useAuth } from "./auth/useAuth";

import type { Chat } from "@/schemas";

const ChatContext = createContext<any>({
  isOpen: false,
  needsAcceptance: false,
});

enum ChatWidgetScreens {
  INBOX = "INBOX",
  SEARCH = "SEARCH",
  CHAT = "CHAT",
  CHAT_SETTINGS = "CHAT_SETTINGS",
}

interface ChatProviderProps {
  children: React.ReactNode;
}

const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const location = useLocation();
  const { currentAccount } = useAuth();
  const updateCredentials = useUpdateCredentials({
    headers: { "Content-Type": "application/json" },
  });

  const path = location.pathname;
  const isUsingMainChatPage = Boolean(path.match(/^\/chats/));
  const { chatId } = useParams<{ chatId: string }>();

  const searchInput = useRef<HTMLInputElement>(null);

  const [screen, setScreen] = useState<ChatWidgetScreens>(
    ChatWidgetScreens.INBOX,
  );
  const [currentChatId, setCurrentChatId] = useState<null | string>(chatId!);

  const { entity: chat } = useChat(currentChatId!);

  const mainWindowState = currentAccount?.settings?.chats?.mainWindow;
  const needsAcceptance =
    !chat?.accepted && chat?.created_by_account !== currentAccount?.id;
  const isOpen = mainWindowState === "open";

  const changeScreen = (
    screen: ChatWidgetScreens,
    currentChatId?: string | null,
  ) => {
    setCurrentChatId(currentChatId || null);
    setScreen(screen);
  };

  const toggleChatPane = () => {
    const state = mainWindowState === "minimized" ? "open" : "minimized";

    updateCredentials.mutate({
      pleroma_settings_store: {
        soapbox_fe: {
          ...currentAccount?.settings,
          chats: {
            ...currentAccount?.settings?.chats,
            mainWindow: state,
          },
        },
      },
    });
  };

  const value = useMemo(
    () => ({
      chat,
      needsAcceptance,
      isOpen,
      isUsingMainChatPage,
      toggleChatPane,
      screen,
      changeScreen,
      currentChatId,
      searchInput,
    }),
    [
      chat,
      currentChatId,
      needsAcceptance,
      isUsingMainChatPage,
      isOpen,
      screen,
      changeScreen,
      searchInput,
    ],
  );

  useEffect(() => {
    if (chatId) {
      setCurrentChatId(chatId);
    } else {
      setCurrentChatId(null);
    }
  }, [chatId]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

interface ChatContextProps {
  chat: Chat | null;
  isOpen: boolean;
  isUsingMainChatPage?: boolean;
  needsAcceptance: boolean;
  toggleChatPane(): void;
  screen: ChatWidgetScreens;
  currentChatId: string | null;
  changeScreen(screen: ChatWidgetScreens, currentChatId?: string | null): void;
  searchInput: React.RefObject<HTMLInputElement>;
}

const useChatContext = (): ChatContextProps => useContext(ChatContext);

export { ChatContext, ChatProvider, useChatContext, ChatWidgetScreens };
